import React from 'react';

const BOOKING_AFFILIATE_ID = process.env.REACT_APP_BOOKING_AFFILIATE_ID || '';

// Parse a race.location string into a destination suitable for hotel search.
// Examples:
//   "Central Park, New York, NY, US" -> "New York"
//   "Chicago, IL, US" -> "Chicago"
//   "Berlin, Germany" -> "Berlin"
//   "Kona, HI, US" -> "Kona"
const parseDestination = (location) => {
  if (!location) return '';
  const parts = location.split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  // Drop trailing "US" / "USA" / state codes, keep the city
  const tail = parts[parts.length - 1].toUpperCase();
  if (tail === 'US' || tail === 'USA' || tail === 'UNITED STATES') {
    // "City, State, US" -> prefer the second-to-last (city or state)
    // "Central Park, New York, NY, US" -> "New York"
    return parts.length >= 3 ? parts[parts.length - 3] : parts[0];
  }
  // International: "Berlin, Germany" -> "Berlin"
  return parts[0];
};

// Booking.com uses YYYY-MM-DD format for check-in/check-out
const formatBookingDate = (d) => {
  if (!d || isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const buildBookingUrl = (destination, raceDate) => {
  // Default window: arrive day before, leave day after
  let checkin = '';
  let checkout = '';
  if (raceDate) {
    const race = new Date(raceDate + (raceDate.includes('T') ? '' : 'T00:00:00'));
    if (!isNaN(race.getTime())) {
      const arrive = new Date(race);
      arrive.setDate(arrive.getDate() - 1);
      const depart = new Date(race);
      depart.setDate(depart.getDate() + 1);
      checkin = formatBookingDate(arrive);
      checkout = formatBookingDate(depart);
    }
  }

  const params = new URLSearchParams();
  if (BOOKING_AFFILIATE_ID) params.set('aid', BOOKING_AFFILIATE_ID);
  if (destination) params.set('ss', destination);
  if (checkin) params.set('checkin', checkin);
  if (checkout) params.set('checkout', checkout);
  params.set('group_adults', '1');
  params.set('no_rooms', '1');
  params.set('lang', 'en-us');

  return `https://www.booking.com/searchresults.html?${params.toString()}`;
};

const HotelSearch = ({ race, compact = false }) => {
  if (!race || !race.location) return null;

  const destination = parseDestination(race.location);
  if (!destination) return null;

  const bookingUrl = buildBookingUrl(destination, race.date);
  const raceDate = race.date ? new Date(race.date + (race.date.includes('T') ? '' : 'T00:00:00')) : null;
  const niceDate = raceDate && !isNaN(raceDate.getTime())
    ? raceDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  if (compact) {
    return (
      <a
        href={bookingUrl}
        target="_blank"
        rel="sponsored noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.6rem 1.2rem',
          background: 'transparent',
          color: '#FF8A3D',
          border: '1.5px solid #FF8A3D',
          borderRadius: '25px',
          textDecoration: 'none',
          fontSize: '0.9rem',
          fontWeight: 700,
        }}
      >
        Find hotels in {destination} {'\u2197'}
      </a>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255, 138, 61, 0.08) 0%, rgba(194, 58, 255, 0.04) 100%)',
      border: '1px solid rgba(255, 138, 61, 0.25)',
      borderRadius: '14px',
      padding: '1.5rem',
      marginTop: '1.5rem',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.75rem',
      }}>
        <span style={{ fontSize: '1.3rem' }} aria-hidden="true">{'\u{1F3E8}'}</span>
        <h3 style={{
          color: '#FFFFFF',
          fontSize: '1.15rem',
          fontWeight: 700,
          margin: 0,
        }}>
          Where to stay in {destination}
        </h3>
      </div>
      <p style={{
        color: '#9ca3af',
        fontSize: '0.9rem',
        lineHeight: 1.6,
        margin: '0 0 1.25rem',
      }}>
        Book a hotel close to the start line or along the course.
        {niceDate && ` We've pre-filled dates for race weekend (${niceDate}).`}
        {' '}Prices and availability on Booking.com.
      </p>
      <a
        href={bookingUrl}
        target="_blank"
        rel="sponsored noopener noreferrer"
        style={{
          display: 'inline-block',
          padding: '0.75rem 1.5rem',
          background: '#FF8A3D',
          color: '#0B0B10',
          border: 'none',
          borderRadius: '25px',
          textDecoration: 'none',
          fontSize: '0.95rem',
          fontWeight: 700,
        }}
      >
        Search hotels in {destination} {'\u2197'}
      </a>
      <p style={{
        color: '#6b7280',
        fontSize: '0.7rem',
        margin: '1rem 0 0',
        fontStyle: 'italic',
      }}>
        Runrora earns a small commission when you book through this link, at no extra cost to you. It helps keep Runrora free.
      </p>
    </div>
  );
};

export default HotelSearch;
